// Mock loans module (spec §16): applications, the running-loan state machine,
// revolving limits, collateral, alerts and the ledger — all on the bank clock.
import type {
  ApplyInput,
  CibilResult,
  Holding,
  KeyFactStatement,
  LoanAlert,
  LoanProduct,
  LoanRecord,
  LoanSummary,
  LoansState,
  PreApprovedOffer,
} from '@/types/loan.types';
import type { StatementEntry } from '@/types/phase2.types';
import {
  LOAN_PRODUCTS,
  OFFER_MULTIPLIERS,
  HOLDINGS,
  BNPL_PARTNERS,
  productById,
  floatingRatePct,
} from '../loanData';
import {
  addMonths,
  aprPct,
  buildSchedule,
  coolingOffDays,
  emiFor,
  feeFor,
  formatLoanDate,
  goldLtvPct,
  gstOn,
  homeLtvPct,
  isRevolving,
  nextEmiDate,
  nextUnpaid,
  paidEmis,
  simpleInterestForDays,
  tenureInDays,
  tierFor,
  MS_DAY,
} from '@/utils/loans';
import { formatIndianCurrency } from '@/utils/currency';
import { ApiError } from '@/api/client';
import { loadDb, saveDb, toPaise, toRupees, type MockDb } from '../db';
import { mockNow } from '../clock';
import { fail, mockId, pause } from '../util';

const inr = (paise: number): string => formatIndianCurrency(toRupees(paise));

// ---------- shared helpers ----------

const alert = (db: MockDb, message: string): void => {
  db.loans.alerts.unshift({ id: mockId('al'), at: mockNow(db), message });
  db.loans.alerts = db.loans.alerts.slice(0, 50);
};

const ledger = (db: MockDb, entry: Omit<StatementEntry, 'id' | 'date'>): void => {
  db.ledger.unshift({ id: mockId('ln'), date: mockNow(db), ...entry });
};

const credit = (db: MockDb, paise: number, description: string): void => {
  db.balancePaise += paise;
  ledger(db, { description, amountRupees: toRupees(paise), category: 'loan disbursal' });
};

const debit = (db: MockDb, paise: number, description: string): void => {
  db.balancePaise = Math.max(0, db.balancePaise - paise);
  ledger(db, { description, amountRupees: -toRupees(paise), category: 'loan repayment' });
};

const running = (loan: LoanRecord): boolean =>
  ['active', 'overdue', 'moratorium', 'unutilised', 'coolingOff', 'disbursed'].includes(loan.state);

const holdingsSupportPaise = (loan: LoanRecord, shockPct: number): number => {
  const ids = loan.details.pledgedHoldingIds ?? [];
  return HOLDINGS.filter((h) => ids.includes(h.id)).reduce(
    (sum, h) => sum + Math.round((h.valueRupees * 100 * (100 - shockPct) * h.ltvPct) / 100 / 100),
    0
  );
};

// ---------- settlement engine: runs on every read, on the bank clock ----------

const settleLoans = (db: MockDb): void => {
  const now = mockNow(db);
  const st: LoansState = db.loans;
  for (const loan of st.records) {
    // Cooling-off ends → running state.
    if (loan.state === 'coolingOff' && loan.coolingOffUntil && now >= loan.coolingOffUntil) {
      loan.state =
        loan.productId === 'EDUCATION' ? 'moratorium' : isRevolving(loan) ? (loan.usedPaise ?? 0) > 0 ? 'active' : 'unutilised' : 'active';
      if (loan.productId === 'EDUCATION') {
        loan.moratoriumUntil = addMonths(now, (loan.details.courseMonths ?? 12) + (loan.details.graceMonths ?? 6));
      }
    }
    // Education moratorium ends → schedule on everything paid out.
    if (loan.state === 'moratorium' && loan.moratoriumUntil && now >= loan.moratoriumUntil) {
      const interestAdded = loan.details.interestMonthly ? 0 : (loan.moratoriumInterestPaise ?? 0);
      loan.outstandingPaise += interestAdded;
      loan.emiPaise = emiFor(loan.outstandingPaise, loan.ratePaPct / 12, loan.tenureMonths ?? 60);
      loan.nextEmiDate = nextEmiDate(now, loan.emiDay ?? 5);
      loan.schedule = buildSchedule(loan.outstandingPaise, loan.ratePaPct / 12, loan.tenureMonths ?? 60, loan.nextEmiDate);
      loan.state = 'active';
      alert(db, `NovaBank: Moratorium on your ${productById(loan.productId).name} has ended. EMI ${inr(loan.emiPaise ?? 0)} starts ${formatLoanDate(loan.nextEmiDate)}.`);
    }
    // EMI collection on the due date (term loans).
    if ((loan.state === 'active' || loan.state === 'overdue') && loan.nextEmiDate && now >= loan.nextEmiDate && loan.schedule.length > 0) {
      const entry = nextUnpaid(loan);
      if (entry) {
        const mandateSucceeds =
          !st.failNextEmi &&
          (loan.repaymentMethod !== 'AUTO_DEBIT' || db.balancePaise >= entry.emiPaise);
        if (st.failNextEmi) st.failNextEmi = false;
        if (mandateSucceeds) {
          entry.paidAt = now;
          debit(db, entry.emiPaise, `EMI ${entry.n} — ${productById(loan.productId).name}`);
          alert(db, `NovaBank: EMI ${inr(entry.emiPaise)} for loan ${loan.id} debited.`);
          if (loan.state === 'overdue') loan.state = 'active';
          if (entry.balanceAfterPaise <= 0) closeLoan(db, loan, now);
          else loan.nextEmiDate = addMonths(entry.dueDate, 1);
        } else if (!entry.failed) {
          entry.failed = true;
          const charge = toPaise(productById(loan.productId).missedEmiChargeRupees);
          loan.lateChargesPaise += charge + gstOn(charge);
          loan.state = 'overdue';
          alert(db, `NovaBank: EMI Due. EMI ${inr(entry.emiPaise)} for loan ${loan.id} could not be debited. A late payment charge plus 18% GST has been added. Pay now from the loan screen.`);
        }
      }
    }
    // 90 days overdue → defaulted.
    if (loan.state === 'overdue') {
      const firstFailed = loan.schedule.find((s) => s.failed && !s.paidAt);
      if (firstFailed && now >= firstFailed.dueDate + 90 * MS_DAY) {
        loan.state = 'defaulted';
        alert(db, `NovaBank: Loan ${loan.id} has been marked Defaulted after 90 days in EMI Due.`);
      }
    }
    // Salary advance: auto-debit on its last day if still open.
    if (loan.productId === 'SALARY_ADVANCE' && loan.state === 'active' && loan.nextEmiDate && now >= loan.nextEmiDate) {
      debit(db, loan.outstandingPaise, 'Salary Advance repayment');
      loan.outstandingPaise = 0;
      closeLoan(db, loan, now);
    }
    // Revolving: monthly interest charge + 12-month renewal.
    if (isRevolving(loan) && running(loan)) {
      const used = loan.usedPaise ?? 0;
      if (used > 0 && loan.interestLastChargedAt) {
        const days = Math.floor((now - loan.interestLastChargedAt) / MS_DAY);
        if (days >= 30) {
          const interest = Math.round((used * (loan.ratePaPct / 100) * days) / 365);
          loan.usedPaise = used + interest;
          loan.interestLastChargedAt = now;
          alert(db, `NovaBank: Interest of ${inr(interest)} charged to your ${productById(loan.productId).name} for the month.`);
        }
      }
      if (loan.renewsAt && now >= loan.renewsAt) {
        loan.renewsAt = addMonths(loan.renewsAt, 12);
        if (loan.productId === 'OVERDRAFT') {
          const fee = toPaise(499) + gstOn(toPaise(499));
          if (db.balancePaise >= fee) debit(db, fee, 'Overdraft renewal fee + GST');
          else loan.usedPaise = (loan.usedPaise ?? 0) + fee;
          alert(db, `NovaBank: Your Digital Overdraft has renewed for 12 months. Renewal fee ${inr(fee)} charged.`);
        } else {
          alert(db, `NovaBank: Your ${productById(loan.productId).name} limit has renewed for 12 months.`);
        }
      }
      // Margin call deadline → partial liquidation.
      if (loan.marginCall && now >= loan.marginCall.dueAt) {
        loan.usedPaise = Math.max(0, (loan.usedPaise ?? 0) - loan.marginCall.shortfallPaise);
        alert(db, `NovaBank: Part of your pledged holdings was sold to cover ${inr(loan.marginCall.shortfallPaise)} on loan ${loan.id}.`);
        loan.marginCall = undefined;
      }
    }
  }
};

const closeLoan = (db: MockDb, loan: LoanRecord, now: number): void => {
  loan.state = 'closed';
  loan.closedAt = now;
  loan.nextEmiDate = undefined;
  if (loan.productId === 'GOLD') {
    loan.goldRelease = { state: 'RELEASE_STARTED', byDate: now + 7 * MS_DAY };
    alert(db, `NovaBank: Loan ${loan.id} is closed. Your gold will be returned within 7 working days.`);
  } else {
    alert(db, `NovaBank: Loan ${loan.id} is fully repaid and closed.`);
  }
};

const load = (): { db: MockDb; now: number } => {
  const db = loadDb();
  settleLoans(db);
  saveDb(db);
  return { db, now: mockNow(db) };
};

const mustLoan = (db: MockDb, loanId: string): LoanRecord => {
  const loan = db.loans.records.find((l) => l.id === loanId);
  if (!loan) fail('NOT_FOUND', 'Loan not found.');
  return loan as LoanRecord;
};

// ---------- products, offers, summary (§3.1, §6.2) ----------

export const getProducts = async (): Promise<LoanProduct[]> => {
  await pause(300);
  load();
  return LOAN_PRODUCTS;
};

export const getPreApprovedOffers = async (): Promise<PreApprovedOffer[]> => {
  await pause(300);
  const { db } = load();
  const st = db.loans;
  return LOAN_PRODUCTS.map((p) => {
    if (p.family === 'GOLD') return { productId: p.id, eligible: true, reason: 'Subject to gold valuation at a branch' };
    if (p.family === 'SECURITIES') return { productId: p.id, eligible: true, reason: 'Depends on the holdings you pledge' };
    if (p.family === 'SALARY') {
      const max = Math.min(Math.floor(st.lastSalaryRupees / 2), p.maxRupees);
      return db.productType === 'SALARY' && st.lastSalaryRupees > 0
        ? { productId: p.id, eligible: true, amountRupees: max }
        : { productId: p.id, eligible: false, reason: 'Salary Advance is for salary account holders with a salary credit.' };
    }
    if (p.family === 'BUSINESS') {
      return st.businessProfile
        ? { productId: p.id, eligible: true, amountRupees: Math.min(st.monthlyIncomeRupees * (OFFER_MULTIPLIERS[p.id] ?? 0), p.maxRupees) }
        : { productId: p.id, eligible: false, reason: 'Business loans need a current account with a verified business profile.' };
    }
    const amount = Math.min(st.monthlyIncomeRupees * (OFFER_MULTIPLIERS[p.id] ?? 0), p.maxRupees);
    return { productId: p.id, eligible: true, amountRupees: amount };
  });
};

export const getLoanSummary = async (): Promise<LoanSummary> => {
  await pause(250);
  const { db } = load();
  const st = db.loans;
  const runningLoans = st.records.filter((l) => ['active', 'overdue', 'moratorium'].includes(l.state));
  const emis = runningLoans
    .filter((l) => l.nextEmiDate && l.emiPaise)
    .sort((a, b) => (a.nextEmiDate ?? 0) - (b.nextEmiDate ?? 0));
  const instantOutstanding = st.records.some((l) => l.productId === 'PERSONAL' && running(l));
  return {
    activeCount: runningLoans.length,
    totalOutstandingRupees: toRupees(runningLoans.reduce((s, l) => s + l.outstandingPaise + (l.usedPaise ?? 0), 0)),
    nextEmi: emis[0] ? { date: emis[0].nextEmiDate as number, amountRupees: toRupees(emis[0].emiPaise ?? 0) } : undefined,
    deregistered: st.deregistered,
    deregisterBlocked: instantOutstanding,
  };
};

// ---------- CIBIL (§6.2, §16) ----------

const scoreFromPan = (pan: string): number => {
  const lastDigit = pan.replace(/[^0-9]/g, '').slice(-1);
  if (lastDigit === '0' || lastDigit === '') return -1;
  return [650, 671, 693, 714, 735, 756, 778, 799, 820][Number(lastDigit) - 1];
};

export const checkCibil = async (productId: string, consent: boolean): Promise<CibilResult> => {
  await pause(500);
  if (!consent) fail('NO_CONSENT', 'Please agree to the credit bureau check to apply.');
  const { db } = load();
  const st = db.loans;
  const product = productById(productId as LoanProduct['id']);
  const score = st.cibilOverride ?? scoreFromPan(st.pan);
  if (score === -1) return { score, eligible: false, message: 'We could not find a credit history for this PAN.' };
  if (score < 700) return { score, eligible: false, message: 'Your credit score does not qualify for this loan right now.' };
  if (product.id === 'PAYLATER') {
    // Fixed band 18–24%: 780+ → 18%, 700 → 24%, straight line between.
    const clamped = Math.min(score, 780);
    const rate = 24 - ((clamped - 700) / 80) * 6;
    return { score, eligible: true, ratePaPct: Math.round(rate * 100) / 100 };
  }
  if (product.id === 'PERSONAL') {
    return { score, eligible: true, message: 'Your score qualifies you. The rate on this loan comes from the amount tier — 0% or 2% per month — never from the score.' };
  }
  return { score, eligible: true, ratePaPct: floatingRatePct(product) };
};

// ---------- eligibility (§6.1, in order) + product rules (§9, §18) ----------

const instantExclusions = (db: MockDb): string | undefined => {
  const st = db.loans;
  if (st.restricted) return 'This account is restricted, so we can\'t offer a loan right now.';
  if (st.deregistered) return 'You have deregistered from Instant Loans.';
  if (st.jointHolder) return 'Joint account holders can\'t apply for this loan.';
  if (st.records.some((l) => ['overdue', 'defaulted'].includes(l.state))) {
    return 'You have an overdue loan or a negative credit record, so we can\'t offer a new loan right now.';
  }
  if (st.ageYears < 18 || st.ageYears > 60) return 'You must be between 18 and 60 to apply for this loan.';
  if (db.balancePaise < 0) return 'Your account is overdrawn. Bring the balance above zero to apply.';
  return undefined;
};

const eligibilityFailure = (db: MockDb, product: LoanProduct, amountPaise: number, tenureMonths: number | undefined): string | undefined => {
  const st = db.loans;
  if (product.id === 'PERSONAL') {
    const excluded = instantExclusions(db);
    if (excluded) return excluded;
  } else {
    const tenureYears = (tenureMonths ?? 12) / 12;
    if (st.ageYears < 21 || st.ageYears + tenureYears > 65) {
      return 'This loan is available from age 21 up to 65 at the end of the loan.';
    }
  }
  if (st.monthlyIncomeRupees < 25_000) return 'A monthly income of at least ₹25,000 is needed for this loan.';
  const score = st.cibilOverride ?? scoreFromPan(st.pan);
  if (score === -1) return 'We could not find a credit history for this PAN.';
  if (score < 700) return 'Your credit score does not qualify for this loan right now.';
  if (amountPaise < toPaise(product.minRupees)) return 'Amount below minimum limit';
  if (product.id === 'PERSONAL') {
    const limitPaise = toPaise(Math.min(st.monthlyIncomeRupees * 20, product.maxRupees));
    if (amountPaise > limitPaise) return 'Amount above maximum limit';
  } else if (amountPaise > toPaise(product.maxRupees)) {
    return `${product.name} is available from ${inr(toPaise(product.minRupees))} to ${inr(toPaise(product.maxRupees))}.`;
  }
  // EMI load: all EMIs including this one within half the income.
  if (!isRevolving({ productId: product.id } as LoanRecord) && product.family !== 'SALARY') {
    const monthlyRate = product.id === 'PERSONAL' ? (tierFor(toPaise(Math.min(st.monthlyIncomeRupees * 20, product.maxRupees)), amountPaise)?.monthlyRatePct ?? 0) : floatingRatePct(product) / 12;
    const newEmi = emiFor(amountPaise, monthlyRate, tenureMonths ?? 12);
    const existing = st.records
      .filter((l) => ['active', 'overdue'].includes(l.state) && l.emiPaise)
      .reduce((s, l) => s + (l.emiPaise ?? 0), 0);
    if (existing + newEmi > toPaise(st.monthlyIncomeRupees) / 2) {
      return `With your existing EMIs, this ${product.name} EMI would cross half of your monthly income.`;
    }
  }
  return undefined;
};

const productRuleFailure = (db: MockDb, product: LoanProduct, input: ApplyInput): string | undefined => {
  const d = input.details;
  const st = db.loans;
  const amountPaise = toPaise(input.amountRupees);
  switch (product.id) {
    case 'HOME': {
      if (!d.propertyType) return 'Choose the property type.';
      if (d.propertyType === 'Under construction' && !d.approvedProject) return 'Choose the approved project for an under-construction home.';
      if (!d.propertyValueRupees) return 'Enter the value of the property.';
      if (amountPaise > (d.propertyValueRupees * 100 * homeLtvPct(amountPaise)) / 100) {
        return `You can borrow up to ${homeLtvPct(amountPaise)}% of the value here, which is ${inr(Math.round((d.propertyValueRupees * 100 * homeLtvPct(amountPaise)) / 100))}.`;
      }
      return undefined;
    }
    case 'LAP': {
      if (d.hasExistingLoan) return 'We can\'t lend against a property that already has a loan on it.';
      if (!d.propertyValueRupees) return 'Enter the value of the property.';
      if (amountPaise > d.propertyValueRupees * 100 * 0.7) {
        return `You can borrow up to 70% of the value here, which is ${inr(Math.round(d.propertyValueRupees * 100 * 0.7))}.`;
      }
      return undefined;
    }
    case 'VEHICLE': {
      if (!d.vehicleType || !d.dealer || !d.onRoadPriceRupees) return 'Choose the vehicle type, the dealer and the on-road price.';
      if (d.vehicleCondition === 'USED' && (input.tenureMonths ?? 0) > 60) return 'A used vehicle can be financed for up to 60 months.';
      if (amountPaise > d.onRoadPriceRupees * 100 * 0.9) {
        return `You can borrow up to 90% of the value here, which is ${inr(Math.round(d.onRoadPriceRupees * 100 * 0.9))}.`;
      }
      return undefined;
    }
    case 'EDUCATION': {
      if (!d.course || !d.institute || !d.courseMonths) return 'Enter the course, the institute and the course length.';
      if (amountPaise > 7_50_000 * 100 && !d.coApplicant && !d.collateralValueRupees) {
        return 'Above ₹7,50,000 we need a co-applicant or collateral.';
      }
      return undefined;
    }
    case 'GOLD': {
      const grams = (d.ornamentGrams ?? 0) + (d.coinGrams ?? 0);
      if (!grams || !d.purityK) return 'Enter the weight and purity of the gold you are pledging.';
      if ((d.ornamentGrams ?? 0) > 1000 || (d.coinGrams ?? 0) > 50) return 'You can pledge up to 1 kg of ornaments and 50 g of coins in total.';
      if (d.repaymentKind === 'BULLET' && (input.tenureMonths ?? 0) > 12) return 'A bullet gold loan runs for up to 12 months.';
      const valuePaise = Math.round((grams * st.goldRateRupeesPerGram * 100 * (d.purityK ?? 24)) / 24);
      const checked = d.repaymentKind === 'BULLET'
        ? amountPaise + Math.round((amountPaise * (floatingRatePct(product) / 100) * (input.tenureMonths ?? 12)) / 12)
        : amountPaise;
      if (checked > (valuePaise * goldLtvPct(checked)) / 100) {
        return `You can borrow up to ${goldLtvPct(checked)}% of the value here, which is ${inr(Math.round((valuePaise * goldLtvPct(checked)) / 100))}.`;
      }
      return undefined;
    }
    case 'BUSINESS':
    case 'WORKING_CAPITAL': {
      if (!st.businessProfile) return 'Business loans need a current account with a verified business profile.';
      return undefined;
    }
    case 'LAMF':
    case 'LAS': {
      const ids = d.pledgedHoldingIds ?? [];
      if (ids.length === 0) return 'Choose the holdings you want to pledge.';
      const support = holdingsSupportPaise({ details: d } as LoanRecord, st.collateralShockPct);
      if (amountPaise > support) return `These holdings support a limit of up to ${inr(support)}.`;
      if (amountPaise > toPaise(1_00_00_000)) return 'Loans against securities are capped at ₹1 crore per person across all lenders.';
      return undefined;
    }
    case 'SALARY_ADVANCE': {
      if (db.productType !== 'SALARY' || st.lastSalaryRupees <= 0) return 'Salary Advance is for salary account holders with a salary credit.';
      const max = Math.min(Math.floor(st.lastSalaryRupees / 2), product.maxRupees);
      if (input.amountRupees > max) return `You can take up to ${inr(toPaise(max))}, half of your last salary.`;
      if (!input.tenureDays || input.tenureDays < 1 || input.tenureDays > 30) return 'A salary advance runs for 1 to 30 days.';
      return undefined;
    }
    default:
      return undefined;
  }
};

export const applyForLoan = async (input: ApplyInput): Promise<LoanRecord> => {
  await pause(500);
  const { db, now } = load();
  const product = productById(input.productId);
  const amountPaise = toPaise(input.amountRupees);

  const firstFailure =
    eligibilityFailure(db, product, amountPaise, input.tenureMonths) ?? productRuleFailure(db, product, input);
  if (firstFailure) fail('RULE', firstFailure);

  const st = db.loans;
  const score = st.cibilOverride ?? scoreFromPan(st.pan);
  const instantLimitPaise = toPaise(Math.min(st.monthlyIncomeRupees * 20, product.maxRupees));
  const tier = product.id === 'PERSONAL' ? tierFor(instantLimitPaise, amountPaise) : undefined;
  const monthlyRatePct = product.id === 'PERSONAL' ? (tier?.monthlyRatePct ?? 0) : undefined;
  const ratePaPct =
    product.id === 'PERSONAL' ? (tier?.monthlyRatePct ?? 0) * 12
    : product.id === 'PAYLATER' ? 24 - ((Math.min(score, 780) - 700) / 80) * 6
    : product.id === 'SALARY_ADVANCE' ? 0
    : floatingRatePct(product);

  const feePaise = product.id === 'PERSONAL'
    ? Math.round((amountPaise * (tier?.feePct ?? 0)) / 100)
    : feeFor(product, amountPaise);
  const gstPaise = gstOn(feePaise);
  const insurancePaise =
    input.insuranceOpted && tier?.insurancePct ? Math.round((amountPaise * tier.insurancePct) / 100) : 0;

  const monthly = monthlyRatePct ?? ratePaPct / 12;
  const isBullet = product.id === 'GOLD' && input.details.repaymentKind === 'BULLET';
  const emiPaise = product.isRevolving || isBullet || product.id === 'SALARY_ADVANCE'
    ? undefined
    : emiFor(amountPaise, monthly, input.tenureMonths ?? 12);

  const goldValue = product.id === 'GOLD'
    ? Math.round((((input.details.ornamentGrams ?? 0) + (input.details.coinGrams ?? 0)) * st.goldRateRupeesPerGram * 100 * (input.details.purityK ?? 24)) / 24)
    : undefined;

  const loan: LoanRecord = {
    id: mockId('loan'),
    productId: product.id,
    // Personal / Short-Term loans are auto-approved in the mock immediately
    // after a successful application; other products remain in bank review.
    state: product.id === 'PERSONAL' ? 'approved' : 'underReview',
    amountPaise,
    tenureMonths: input.tenureMonths,
    tenureDays: input.tenureDays,
    ratePaPct: Math.round(ratePaPct * 100) / 100,
    monthlyRatePct,
    insuranceOpted: input.insuranceOpted,
    feePaise,
    gstPaise,
    insurancePaise,
    emiPaise,
    emiDay: input.emiDay,
    repaymentMethod: input.repaymentMethod,
    kfsAccepted: false,
    documentsUploaded: [],
    appliedAt: now,
    outstandingPaise: amountPaise,
    lateChargesPaise: 0,
    schedule: [],
    details: input.details,
    cibilScore: score,
    usedPaise: product.isRevolving ? 0 : undefined,
    drawingEnabled: product.id === 'OVERDRAFT' ? true : undefined,
    goldValuePaise: goldValue,
    goldRelease: product.id === 'GOLD' ? { state: 'PLEDGED' } : undefined,
    propertyVerified: ['HOME', 'LAP'].includes(product.id) ? false : undefined,
    stagesPaid: product.id === 'HOME' && input.details.propertyType === 'Under construction' ? 0 : undefined,
    preApproved: product.id === 'PERSONAL' && amountPaise <= instantLimitPaise,
  };
  if (isBullet) {
    loan.bulletDuePaise = amountPaise + Math.round((amountPaise * (ratePaPct / 100) * (input.tenureMonths ?? 12)) / 12);
    loan.bulletDueDate = addMonths(now, input.tenureMonths ?? 12);
  }
  st.records.unshift(loan);
  st.pledgedAcrossLoansGrams.ornaments += input.details.ornamentGrams ?? 0;
  st.pledgedAcrossLoansGrams.coins += input.details.coinGrams ?? 0;
  saveDb(db);
  alert(db, `NovaBank: Your ${product.name} application is submitted. The bank is reviewing it.`);
  saveDb(db);
  return loan;
};

export const getApplications = async (): Promise<LoanRecord[]> => {
  await pause(300);
  const { db } = load();
  return db.loans.records.filter((l) => ['underReview', 'approved', 'rejected'].includes(l.state));
};

// ---------- figures + KFS (§10, §11) ----------

export const figuresFor = (loan: LoanRecord, now: number): {
  feePaise: number; gstPaise: number; insurancePaise: number; totalChargesPaise: number;
  takeHomePaise: number; emiPaise?: number; totalInterestPaise: number; totalCostPaise: number;
  apr: number; firstEmiDate?: number; coolingOffDays: number;
} => {
  const totalChargesPaise = loan.feePaise + loan.gstPaise + loan.insurancePaise;
  const takeHomePaise = loan.amountPaise - totalChargesPaise;
  const monthly = loan.monthlyRatePct ?? loan.ratePaPct / 12;
  const term = loan.tenureMonths ?? 12;
  const emiPaise = loan.emiPaise ?? (loan.schedule.length || loan.bulletDueDate || isRevolving(loan) || loan.productId === 'SALARY_ADVANCE' ? undefined : emiFor(loan.amountPaise, monthly, term));
  const totalInterestPaise = loan.bulletDueDate
    ? (loan.bulletDuePaise ?? loan.amountPaise) - loan.amountPaise
    : emiPaise ? emiPaise * term - loan.amountPaise : 0;
  const payments = loan.bulletDueDate
    ? [loan.bulletDuePaise ?? loan.amountPaise]
    : emiPaise ? Array.from({ length: term }, () => emiPaise) : [];
  const apr = payments.length ? aprPct(takeHomePaise, payments) : 0;
  return {
    feePaise: loan.feePaise,
    gstPaise: loan.gstPaise,
    insurancePaise: loan.insurancePaise,
    totalChargesPaise,
    takeHomePaise,
    emiPaise,
    totalInterestPaise,
    totalCostPaise: totalChargesPaise + totalInterestPaise,
    apr,
    firstEmiDate: emiPaise ? nextEmiDate(now, loan.emiDay ?? 5) : undefined,
    coolingOffDays: coolingOffDays(tenureInDays(loan.tenureMonths, loan.tenureDays)),
  };
};

export const getKeyFactStatement = async (loanId: string): Promise<KeyFactStatement> => {
  await pause(300);
  const { db, now } = load();
  const loan = mustLoan(db, loanId);
  const product = productById(loan.productId);
  const fig = figuresFor(loan, now);
  const d = loan.details;
  const fmt = inr;

  const whatYouGet = [
    { label: 'Sanctioned amount', value: fmt(loan.amountPaise) },
    { label: 'Processing fee', value: fmt(fig.feePaise) },
    { label: 'GST on the fee (18%)', value: fmt(fig.gstPaise) },
    ...(loan.insuranceOpted ? [{ label: 'Credit life insurance', value: fmt(fig.insurancePaise) }] : []),
    { label: 'Total charges', value: fmt(fig.totalChargesPaise) },
    { label: 'Take home amount', value: fmt(fig.takeHomePaise) },
  ];
  const whatItCosts = [
    { label: 'Interest rate', value: loan.monthlyRatePct === 0 ? 'No interest' : `${loan.ratePaPct.toFixed(2)}% p.a. reducing balance${loan.monthlyRatePct ? ` (${loan.monthlyRatePct}% a month)` : ''}` },
    { label: 'APR (fee-inclusive)', value: `~${fig.apr.toFixed(2)}%` },
    ...(fig.emiPaise ? [{ label: 'Monthly EMI', value: fmt(fig.emiPaise) }] : []),
    { label: 'Tenure', value: loan.tenureDays ? `${loan.tenureDays} days` : `${loan.tenureMonths} months` },
    { label: 'Total interest', value: fmt(fig.totalInterestPaise) },
    { label: 'Total payable', value: fmt(loan.amountPaise + fig.totalInterestPaise) },
  ];
  const ifWrong = [
    { label: 'Missed EMI', value: `${inr(toPaise(product.missedEmiChargeRupees))} flat plus 18% GST, never extra interest` },
    { label: 'Foreclosure', value: 'Free — floating-rate loans carry no charge' },
  ];

  const bullets: string[] = [];
  if (product.id === 'GOLD') {
    bullets.push(
      `${(d.ornamentGrams ?? 0) + (d.coinGrams ?? 0)} g of ${d.purityK}K gold, worth ${inr(loan.goldValuePaise ?? 0)}.`,
      d.repaymentKind === 'BULLET' ? 'Bullet repayment: one payment of principal plus simple interest at maturity.' : 'EMI repayment.',
      'If the loan defaults, we send two notices 14 days apart before any auction. The reserve price is at least 90% of the gold\'s value at the IBJA rate, and any money left after the loan is paid off goes back to you within 7 working days.',
      'Your gold will be returned within 7 working days of closing the loan.'
    );
  }
  if (product.id === 'VEHICLE') bullets.push(`${d.vehicleCondition === 'USED' ? 'Used' : 'New'} ${d.vehicleType} from ${d.dealer}, on-road price ${inr(toPaise(d.onRoadPriceRupees ?? 0))}.`, 'The bank\'s hypothecation stays on the vehicle\'s RC until the loan closes.');
  if (product.id === 'HOME') bullets.push(`${d.propertyType}${d.approvedProject ? ` — ${d.approvedProject}` : ''}, value ${inr(toPaise(d.propertyValueRupees ?? 0))}.`, ...(d.propertyType === 'Under construction' ? ['Paid out in 4 stages as construction moves along.'] : []), 'Approval follows legal and technical verification of the property.');
  if (product.id === 'LAP') bullets.push(`${d.propertyType} property for ${(d.lapPurpose ?? 'personal').toLowerCase()} use, value ${inr(toPaise(d.propertyValueRupees ?? 0))}.`, 'Approval follows legal and technical verification of the property.');
  if (product.id === 'EDUCATION') bullets.push(`${d.course} at ${d.institute}, ${d.courseMonths} months.`, 'Fees go straight to the institute each semester, not to your account.', `Moratorium: the course plus ${d.graceMonths} months — interest ${d.interestMonthly ? 'paid monthly from your account' : 'added to the loan when EMIs start'}.`);
  if (['LAMF', 'LAS'].includes(product.id)) bullets.push('Pledged means not sold: your holdings stay yours and keep earning.', 'The ₹1 crore limit on loans against securities counts across all lenders.', 'If the pledged value falls, we may ask you to pledge more or repay within 7 days before any holding is sold.');
  if (product.id === 'OVERDRAFT') bullets.push('Set-up fee ₹999 plus GST; renewal fee ₹499 plus GST each year. Interest is charged only on what you use, worked out daily and taken monthly.');
  if (['WORKING_CAPITAL', 'LAMF', 'LAS'].includes(product.id)) bullets.push('Interest is charged only on what you use, worked out daily and taken monthly. The limit renews every 12 months.');
  if (product.id === 'PAYLATER') bullets.push(`30 days interest-free on every purchase, then ${loan.ratePaPct}% a year on what is unpaid.`, 'A refund lowers what you owe, but interest and charges already paid are not refunded.');
  if (product.id === 'SALARY_ADVANCE') bullets.push(`Repaid automatically when your next salary is credited, or on day ${loan.tenureDays} at the latest.`);
  if (product.isRevolving) bullets.push('Any limit increase needs your fresh consent.');

  return {
    loanId: loan.id,
    productName: product.name,
    coolingOffDays: fig.coolingOffDays,
    sections: [
      { title: 'What you get', rows: whatYouGet },
      { title: 'What it costs', rows: whatItCosts },
      { title: 'If things go wrong', rows: ifWrong },
      { title: 'Your cooling-off right', rows: [{ label: `${fig.coolingOffDays} days`, value: 'Return the principal plus interest for the days held and the loan closes. The processing fee is not refunded.' }] },
      ...(bullets.length ? [{ title: 'Terms for this product', rows: [], bullets }] : []),
      { title: 'Who to complain to', rows: [
        { label: 'Grievance officer', value: 'Meera Krishnan' },
        { label: 'Email', value: 'loans.grievance@novabank.in' },
        { label: 'Phone', value: '1800 266 4321' },
        { label: 'RBI ombudsman', value: 'https://cms.rbi.org.in (RBI Integrated Ombudsman)' },
      ] },
    ],
  };
};

export const acknowledgeKfs = async (loanId: string): Promise<void> => {
  await pause(250);
  const { db } = load();
  mustLoan(db, loanId).kfsAccepted = true;
  saveDb(db);
};

export const submitDocuments = async (loanId: string, documents: string[]): Promise<void> => {
  await pause(300);
  const { db } = load();
  const loan = mustLoan(db, loanId);
  loan.documentsUploaded = documents;
  saveDb(db);
};

/** Insurance can be switched right up to disbursal; figures move, acceptance stays (§10). */
export const setInsurance = async (loanId: string, opted: boolean): Promise<LoanRecord> => {
  await pause(250);
  const { db } = load();
  const loan = mustLoan(db, loanId);
  if (!['underReview', 'approved'].includes(loan.state)) fail('RULE', 'This loan is not running.');
  const st = db.loans;
  const tier = loan.productId === 'PERSONAL'
    ? tierFor(toPaise(Math.min(st.monthlyIncomeRupees * 20, productById('PERSONAL').maxRupees)), loan.amountPaise)
    : undefined;
  loan.insuranceOpted = opted;
  loan.insurancePaise = opted && tier?.insurancePct ? Math.round((loan.amountPaise * tier.insurancePct) / 100) : 0;
  saveDb(db);
  return loan;
};

export const cancelApplication = async (loanId: string): Promise<void> => {
  await pause(300);
  const { db } = load();
  const loan = mustLoan(db, loanId);
  if (!['underReview', 'approved'].includes(loan.state)) fail('RULE', 'This loan is not running.');
  loan.state = 'cancelled';
  saveDb(db);
};

// ---------- disbursal (§8.4) ----------

export const disburseLoan = async (loanId: string): Promise<LoanRecord> => {
  await pause(500);
  const { db, now } = load();
  const loan = mustLoan(db, loanId);
  if (loan.state === 'underReview') fail('RULE', 'The bank has not approved this application yet.');
  if (loan.state !== 'approved') fail('RULE', 'This loan is not running.');
  const product = productById(loan.productId);
  const fig = figuresFor(loan, now);
  const days = fig.coolingOffDays;

  if (product.isRevolving) {
    // Nothing paid out at sanction; set-up fee + GST is debited (§13).
    const setup = loan.feePaise + loan.gstPaise;
    if (setup > 0) debit(db, setup, `${product.name} set-up fee + GST`);
    loan.usedPaise = 0;
    loan.interestLastChargedAt = now;
    loan.renewsAt = addMonths(now, 12);
  } else if (product.id === 'EDUCATION') {
    loan.paidToInstitutePaise = loan.amountPaise;
    loan.moratoriumUntil = addMonths(now, (loan.details.courseMonths ?? 12) + (loan.details.graceMonths ?? 6));
  } else if (product.id === 'HOME' && loan.details.propertyType === 'Under construction') {
    loan.stagesPaid = 1;
    credit(db, Math.round(loan.amountPaise / 4), `${product.name} — stage 1 of 4`);
  } else {
    credit(db, fig.takeHomePaise, `${product.name} disbursal`);
  }

  loan.disbursedAt = now;
  loan.state = 'coolingOff';
  loan.coolingOffUntil = db.limits.removeTimeLimits ? now : now + days * MS_DAY;
  loan.outstandingPaise = loan.amountPaise;

  if (loan.emiPaise) {
    loan.nextEmiDate = nextEmiDate(now, loan.emiDay ?? 5);
    loan.schedule = buildSchedule(loan.amountPaise, loan.monthlyRatePct ?? loan.ratePaPct / 12, loan.tenureMonths ?? 12, loan.nextEmiDate);
  }
  if (loan.productId === 'SALARY_ADVANCE') {
    loan.nextEmiDate = now + (loan.tenureDays ?? 30) * MS_DAY;
  }
  if (loan.bulletDueDate) loan.bulletDueDate = addMonths(now, loan.tenureMonths ?? 12);

  saveDb(db);
  alert(db, product.isRevolving
    ? `NovaBank: Your ${product.name} of ${inr(loan.amountPaise)} is sanctioned. Use it when you need it.`
    : `NovaBank: ${inr(fig.takeHomePaise)} from your ${product.name} has been disbursed.`);
  saveDb(db);
  return loan;
};

// ---------- running-loan reads (§12) ----------

export const getLoans = async (): Promise<LoanRecord[]> => {
  await pause(300);
  const { db } = load();
  return db.loans.records;
};

export const getLoan = async (loanId: string): Promise<LoanRecord> => {
  await pause(300);
  const { db } = load();
  return mustLoan(db, loanId);
};

export const getSchedule = async (loanId: string) => {
  await pause(300);
  const { db } = load();
  return mustLoan(db, loanId).schedule;
};

export const getAlerts = async (): Promise<LoanAlert[]> => {
  const { db } = load();
  return db.loans.alerts;
};

// ---------- running-loan actions (§12) ----------

export const payEmiNow = async (loanId: string): Promise<LoanRecord> => {
  await pause(400);
  const { db, now } = load();
  const loan = mustLoan(db, loanId);
  if (!['active', 'overdue'].includes(loan.state)) fail('RULE', 'This loan is not running.');

  if (loan.productId === 'SALARY_ADVANCE') {
    if (db.balancePaise < loan.outstandingPaise) fail('RULE', `Insufficient balance. Available: ${inr(db.balancePaise)}.`);
    debit(db, loan.outstandingPaise, 'Salary Advance repayment');
    loan.outstandingPaise = 0;
    closeLoan(db, loan, now);
    saveDb(db);
    return loan;
  }
  if (loan.bulletDueDate) {
    const due = (loan.bulletDuePaise ?? 0) + loan.lateChargesPaise;
    if (db.balancePaise < due) fail('RULE', `Insufficient balance. Available: ${inr(db.balancePaise)}.`);
    debit(db, due, 'Bullet gold repayment');
    loan.outstandingPaise = 0;
    loan.lateChargesPaise = 0;
    closeLoan(db, loan, now);
    saveDb(db);
    return loan;
  }
  const entry = nextUnpaid(loan);
  if (!entry) throw new ApiError('RULE', 'This loan is not running.');
  const duePaise = entry.emiPaise + (entry.failed ? loan.lateChargesPaise : 0);
  if (db.balancePaise < duePaise) fail('RULE', `Insufficient balance. Available: ${inr(db.balancePaise)}.`);
  debit(db, duePaise, `EMI ${entry.n} — ${productById(loan.productId).name}${entry.failed ? ' (incl. late charge)' : ''}`);
  entry.paidAt = now;
  loan.lateChargesPaise = 0;
  if (loan.state === 'overdue') loan.state = 'active';
  if (entry.balanceAfterPaise <= 0) closeLoan(db, loan, now);
  else loan.nextEmiDate = nextUnpaid(loan)?.dueDate ?? addMonths(entry.dueDate, 1);
  saveDb(db);
  alert(db, `NovaBank: EMI ${inr(duePaise)} for loan ${loan.id} paid.`);
  saveDb(db);
  return loan;
};

export const exitDuringCoolingOff = async (loanId: string): Promise<LoanRecord> => {
  await pause(400);
  const { db, now } = load();
  const loan = mustLoan(db, loanId);
  if (loan.state !== 'coolingOff') fail('RULE', 'The cooling-off period is over. You can foreclose the loan instead.');
  const daysHeld = Math.max(1, Math.ceil((now - (loan.disbursedAt ?? now)) / MS_DAY));
  const interest = simpleInterestForDays(loan.amountPaise, loan.ratePaPct, daysHeld);
  const due = loan.amountPaise + interest;
  if (db.balancePaise < due) fail('RULE', `Insufficient balance. Available: ${inr(db.balancePaise)}.`);
  debit(db, due, `Cooling-off exit — ${productById(loan.productId).name}`);
  loan.state = 'coolingOffExited';
  loan.closedAt = now;
  loan.outstandingPaise = 0;
  saveDb(db);
  alert(db, 'NovaBank: Loan cancelled. The money was returned.');
  saveDb(db);
  return loan;
};

export const forecloseLoan = async (loanId: string): Promise<LoanRecord> => {
  await pause(400);
  const { db, now } = load();
  const loan = mustLoan(db, loanId);
  if (!['active', 'overdue', 'coolingOff', 'moratorium', 'unutilised'].includes(loan.state)) fail('RULE', 'This loan is not running.');
  const due = isRevolving(loan)
    ? (loan.usedPaise ?? 0)
    : loan.bulletDueDate
      ? (loan.bulletDuePaise ?? 0) + loan.lateChargesPaise
      : loan.outstandingPaise + loan.lateChargesPaise;
  if (db.balancePaise < due) fail('RULE', `Insufficient balance. Available: ${inr(db.balancePaise)}.`);
  debit(db, due, `Foreclosure — ${productById(loan.productId).name}`);
  loan.outstandingPaise = 0;
  loan.usedPaise = isRevolving(loan) ? 0 : loan.usedPaise;
  loan.lateChargesPaise = 0;
  loan.state = 'foreclosed';
  loan.closedAt = now;
  if (loan.productId === 'GOLD') loan.goldRelease = { state: 'RELEASE_STARTED', byDate: now + 7 * MS_DAY };
  saveDb(db);
  alert(db, `NovaBank: Loan ${loan.id} foreclosed. No charge on floating-rate loans.`);
  saveDb(db);
  return loan;
};

/** Part-prepayment (§12.1): EMI loans only, never bullet / revolving / advance. */
export const partPrepay = async (
  loanId: string,
  amountRupees: number,
  mode: 'REDUCE_EMI' | 'REDUCE_TENURE'
): Promise<LoanRecord> => {
  await pause(400);
  const { db } = load();
  const loan = mustLoan(db, loanId);
  if (loan.state !== 'active' || !loan.schedule.length || loan.bulletDueDate || isRevolving(loan) || loan.productId === 'SALARY_ADVANCE') {
    fail('RULE', 'This loan is not running.');
  }
  if (!amountRupees || amountRupees <= 0) fail('RULE', 'Enter a valid amount');
  if (amountRupees < 1_000) fail('RULE', 'Part-prepayment starts at ₹1,000.00.');
  const paise = toPaise(amountRupees);
  if (paise >= loan.outstandingPaise) fail('RULE', 'Amount to repay is greater than Outstanding loan Balance');
  if (db.balancePaise < paise) fail('RULE', `Insufficient balance. Available: ${inr(db.balancePaise)}.`);
  debit(db, paise, `Part-prepayment — ${productById(loan.productId).name}`);

  const monthly = loan.monthlyRatePct ?? loan.ratePaPct / 12;
  const remaining = loan.schedule.filter((s) => !s.paidAt);
  const firstDue = remaining[0]?.dueDate ?? Date.now();
  const newOutstanding = loan.outstandingPaise - paise;
  if (mode === 'REDUCE_EMI') {
    const newEmi = emiFor(newOutstanding, monthly, remaining.length);
    remaining.forEach((s, i) => {
      s.emiPaise = i === remaining.length - 1 ? 0 : newEmi; // recomputed below
    });
    let balance = newOutstanding;
    remaining.forEach((s, i) => {
      const interest = Math.round(balance * (monthly / 100));
      let principal = (i === remaining.length - 1 ? balance + interest : newEmi) - interest;
      if (i === remaining.length - 1) { principal = balance; s.emiPaise = balance + interest; } else s.emiPaise = newEmi;
      balance = Math.max(0, balance - principal);
      s.interestPaise = interest;
      s.principalPaise = principal;
      s.balanceAfterPaise = balance;
    });
    loan.emiPaise = newEmi;
  } else {
    // EMI stays; count months needed to clear at the current EMI.
    let balance = newOutstanding;
    let months = 0;
    while (balance > 0 && months < 1200) {
      const interest = Math.round(balance * (monthly / 100));
      balance -= (loan.emiPaise ?? 0) - interest;
      months += 1;
    }
    loan.schedule = [
      ...loan.schedule.filter((s) => s.paidAt),
      ...buildSchedule(newOutstanding, monthly, months, firstDue).map((s) => ({ ...s, n: s.n + paidEmis(loan) })),
    ];
    loan.tenureMonths = paidEmis(loan) + months;
  }
  loan.outstandingPaise = newOutstanding;
  saveDb(db);
  alert(db, 'NovaBank: Part-prepayment received.');
  saveDb(db);
  return loan;
};

/** Top-up (personal loan only): adds principal and re-prices the EMI. */
export const topUpLoan = async (loanId: string, amountRupees: number): Promise<LoanRecord> => {
  await pause(400);
  const { db, now } = load();
  const loan = mustLoan(db, loanId);
  if (loan.productId !== 'PERSONAL' || loan.state !== 'active') fail('RULE', 'This loan is not running.');
  const st = db.loans;
  const limitPaise = toPaise(Math.min(st.monthlyIncomeRupees * 20, productById('PERSONAL').maxRupees));
  const paise = toPaise(amountRupees);
  if (loan.outstandingPaise + paise > limitPaise) fail('RULE', 'Amount above maximum limit');
  credit(db, paise, 'Personal loan top-up');
  const newOutstanding = loan.outstandingPaise + paise;
  const monthly = loan.monthlyRatePct ?? 0;
  const remainingMonths = loan.schedule.filter((s) => !s.paidAt).length || (loan.tenureMonths ?? 6);
  loan.emiPaise = emiFor(newOutstanding, monthly, remainingMonths);
  loan.outstandingPaise = newOutstanding;
  loan.schedule = [
    ...loan.schedule.filter((s) => s.paidAt),
    ...buildSchedule(newOutstanding, monthly, remainingMonths, nextEmiDate(now, loan.emiDay ?? 5)).map((s) => ({ ...s, n: s.n + paidEmis(loan) })),
  ];
  saveDb(db);
  alert(db, `NovaBank: Top-up of ${inr(paise)} credited. New EMI ${inr(loan.emiPaise ?? 0)}.`);
  saveDb(db);
  return loan;
};

export const changeRepaymentMethod = async (loanId: string, method: LoanRecord['repaymentMethod']): Promise<void> => {
  await pause(300);
  const { db } = load();
  const loan = mustLoan(db, loanId);
  if (loan.state === 'overdue') fail('RULE', 'An EMI is overdue. Pay the arrears before making changes.');
  loan.repaymentMethod = method;
  saveDb(db);
};

export const changeEmiDay = async (loanId: string, day: number): Promise<void> => {
  await pause(300);
  const { db, now } = load();
  const loan = mustLoan(db, loanId);
  if (loan.state === 'overdue') fail('RULE', 'An EMI is overdue. Pay the arrears before making changes.');
  loan.emiDay = day;
  if (loan.state === 'active' && loan.nextEmiDate) loan.nextEmiDate = nextEmiDate(now, day);
  saveDb(db);
};

// ---------- revolving limits (§13) ----------

export const drawFromLimit = async (loanId: string, amountRupees: number): Promise<LoanRecord> => {
  await pause(400);
  const { db } = load();
  const loan = mustLoan(db, loanId);
  if (!running(loan) || !isRevolving(loan)) fail('RULE', 'This loan is not running.');
  if (loan.marginCall) fail('RULE', 'A margin call is open. Pledge more or repay before drawing.');
  if (loan.productId === 'OVERDRAFT' && loan.drawingEnabled === false) {
    fail('RULE', 'Drawing is switched off on this limit. Switch it on to use it.');
  }
  const available = loan.amountPaise - (loan.usedPaise ?? 0);
  const paise = toPaise(amountRupees);
  if (paise <= 0) fail('RULE', 'Enter a valid amount');
  if (paise > available) fail('RULE', `You can draw up to ${inr(available)} from this limit.`);
  loan.usedPaise = (loan.usedPaise ?? 0) + paise;
  loan.state = 'active';
  credit(db, paise, `${productById(loan.productId).name} drawdown`);
  saveDb(db);
  return loan;
};

export const repayToLimit = async (loanId: string, amountRupees: number): Promise<LoanRecord> => {
  await pause(400);
  const { db } = load();
  const loan = mustLoan(db, loanId);
  if (!isRevolving(loan)) fail('RULE', 'This loan is not running.');
  const paise = toPaise(amountRupees);
  if (paise <= 0) fail('RULE', 'Enter a valid amount');
  if (db.balancePaise < paise) fail('RULE', `Insufficient balance. Available: ${inr(db.balancePaise)}.`);
  // PayLater pays the oldest purchase first (§13.3).
  if (loan.productId === 'PAYLATER' && loan.purchases) {
    let left = paise;
    for (const p of loan.purchases) {
      const pay = Math.min(left, p.outstandingPaise);
      p.outstandingPaise -= pay;
      left -= pay;
      if (left <= 0) break;
    }
  }
  loan.usedPaise = Math.max(0, (loan.usedPaise ?? 0) - paise);
  if (loan.marginCall && (loan.usedPaise ?? 0) <= holdingsSupportPaise(loan, db.loans.collateralShockPct)) {
    loan.marginCall = undefined;
    alert(db, `NovaBank: Margin call on loan ${loan.id} cleared.`);
  }
  if ((loan.usedPaise ?? 0) === 0 && loan.state === 'active') loan.state = 'unutilised';
  debit(db, paise, `${productById(loan.productId).name} repayment`);
  saveDb(db);
  return loan;
};

export const setDrawingEnabled = async (loanId: string, enabled: boolean): Promise<void> => {
  await pause(300);
  const { db } = load();
  const loan = mustLoan(db, loanId);
  if (loan.productId !== 'OVERDRAFT') fail('RULE', 'This loan is not running.');
  loan.drawingEnabled = enabled;
  saveDb(db);
};

export const increaseLoanLimit = async (loanId: string, newLimitRupees: number, consent: boolean): Promise<LoanRecord> => {
  await pause(400);
  const { db } = load();
  const loan = mustLoan(db, loanId);
  if (!isRevolving(loan)) fail('RULE', 'This loan is not running.');
  if (!consent) fail('RULE', 'Please confirm you want a higher limit.');
  const product = productById(loan.productId);
  const paise = toPaise(newLimitRupees);
  if (paise <= loan.amountPaise) fail('RULE', 'Enter a limit above today\'s.');
  if (paise > toPaise(product.maxRupees)) fail('RULE', `${product.name} is available from ${inr(toPaise(product.minRupees))} to ${inr(toPaise(product.maxRupees))}.`);
  if (['LAMF', 'LAS'].includes(loan.productId)) {
    const support = holdingsSupportPaise(loan, db.loans.collateralShockPct);
    if (paise > support) fail('RULE', `These holdings support a limit of up to ${inr(support)}.`);
    if (paise > toPaise(1_00_00_000)) fail('RULE', 'Loans against securities are capped at ₹1 crore per person across all lenders.');
  }
  loan.amountPaise = paise;
  saveDb(db);
  alert(db, `NovaBank: Your ${product.name} limit is now ${inr(paise)}.`);
  saveDb(db);
  return loan;
};

// ---------- PayLater (§13.3) ----------

export const bnplPurchase = async (
  loanId: string,
  partnerCode: string,
  purchaseCode: string,
  amountRupees: number
): Promise<LoanRecord> => {
  await pause(400);
  const { db } = load();
  const loan = mustLoan(db, loanId);
  if (loan.productId !== 'PAYLATER' || !running(loan)) fail('RULE', 'This loan is not running.');
  const partner = BNPL_PARTNERS.find((p) => p.code === partnerCode.trim().toUpperCase());
  if (!partner) throw new ApiError('RULE', 'This partner code is not valid.');
  if (!/^[A-Za-z0-9]{6,12}$/.test(purchaseCode.trim())) fail('RULE', 'Enter the purchase code from the store, 6 to 12 letters or digits.');
  const paise = toPaise(amountRupees);
  const available = loan.amountPaise - (loan.usedPaise ?? 0);
  if (paise <= 0) fail('RULE', 'Enter a valid amount');
  if (paise > available) fail('RULE', `You can draw up to ${inr(available)} from this limit.`);
  loan.purchases = loan.purchases ?? [];
  loan.purchases.unshift({
    id: mockId('bnpl'),
    partnerCode: partner.code,
    partnerName: partner.name,
    purchaseCode: purchaseCode.trim(),
    amountPaise: paise,
    outstandingPaise: paise,
    refundedPaise: 0,
    purchasedAt: mockNow(db),
  });
  loan.usedPaise = (loan.usedPaise ?? 0) + paise;
  loan.state = 'active';
  saveDb(db);
  alert(db, `NovaBank: ${inr(paise)} paid to ${partner.name} from your PayLater limit.`);
  saveDb(db);
  return loan;
};

export const bnplRefund = async (loanId: string, purchaseId: string, amountRupees: number): Promise<LoanRecord> => {
  await pause(400);
  const { db } = load();
  const loan = mustLoan(db, loanId);
  const purchase = loan.purchases?.find((p) => p.id === purchaseId);
  if (!purchase) throw new ApiError('RULE', 'Purchase not found.');
  const paise = Math.min(toPaise(amountRupees), purchase.outstandingPaise);
  purchase.outstandingPaise -= paise;
  purchase.refundedPaise += paise;
  loan.usedPaise = Math.max(0, (loan.usedPaise ?? 0) - paise);
  saveDb(db);
  alert(db, `NovaBank: Refund of ${inr(paise)} received from ${purchase.partnerName}. Interest and charges already paid are not refunded.`);
  saveDb(db);
  return loan;
};

// ---------- collateral (§13.1, §9.1) ----------

export const getSecurityHoldings = async (): Promise<Array<Holding & { pledged: boolean }>> => {
  await pause(300);
  const { db } = load();
  const pledgedIds = new Set(
    db.loans.records
      .filter((l) => ['LAMF', 'LAS'].includes(l.productId) && running(l))
      .flatMap((l) => l.details.pledgedHoldingIds ?? [])
  );
  const shock = db.loans.collateralShockPct;
  return HOLDINGS.map((h) => ({
    ...h,
    valueRupees: Math.round((h.valueRupees * (100 - shock)) / 100),
    pledged: pledgedIds.has(h.id),
  }));
};

export const pledgeMore = async (loanId: string, holdingIds: string[]): Promise<LoanRecord> => {
  await pause(400);
  const { db } = load();
  const loan = mustLoan(db, loanId);
  if (!['LAMF', 'LAS'].includes(loan.productId) || !running(loan)) fail('RULE', 'This loan is not running.');
  loan.details.pledgedHoldingIds = [...(loan.details.pledgedHoldingIds ?? []), ...holdingIds];
  if (loan.marginCall && (loan.usedPaise ?? 0) <= holdingsSupportPaise(loan, db.loans.collateralShockPct)) {
    loan.marginCall = undefined;
    alert(db, `NovaBank: Margin call on loan ${loan.id} cleared.`);
  }
  saveDb(db);
  return loan;
};

export const goldRatePerGramPaise = async (): Promise<number> => {
  const { db } = load();
  return db.loans.goldRateRupeesPerGram * 100;
};

// ---------- deregistration (§3.1) ----------

export const deregisterInstantLoan = async (): Promise<void> => {
  await pause(300);
  const { db } = load();
  const blocked = db.loans.records.some((l) => l.productId === 'PERSONAL' && running(l));
  if (blocked) fail('RULE', 'You can deregister once your instant loans are fully repaid.');
  db.loans.deregistered = true;
  saveDb(db);
};

// Deregistration is reversible — the hub offers "Register again" once deregistered.
export const registerInstantLoan = async (): Promise<void> => {
  await pause(300);
  const { db } = load();
  db.loans.deregistered = false;
  saveDb(db);
};

// ---------- test-panel actions (§17) ----------

export const testApprove = (loanId: string): void => {
  const db = loadDb();
  const loan = db.loans.records.find((l) => l.id === loanId);
  if (!loan) return;
  loan.state = 'approved';
  loan.approvedAt = mockNow(db);
  saveDb(db);
  alert(db, `NovaBank: Your ${productById(loan.productId).name} application is approved. Accept & disburse from the Loans hub.`);
  saveDb(db);
};

export const testReject = (loanId: string, reason: string): void => {
  const db = loadDb();
  const loan = db.loans.records.find((l) => l.id === loanId);
  if (!loan) return;
  loan.state = 'rejected';
  loan.rejectReason = reason || 'Not approved';
  saveDb(db);
};

export const testSetCibil = (score?: number): void => {
  const db = loadDb();
  db.loans.cibilOverride = score;
  saveDb(db);
};

export const testFailNextEmi = (): void => {
  const db = loadDb();
  db.loans.failNextEmi = true;
  saveDb(db);
};

/** Collateral shock (§17): drops holdings values and may open a margin call. */
export const testCollateralShock = (pct: number): void => {
  const db = loadDb();
  db.loans.collateralShockPct = pct;
  const now = mockNow(db);
  for (const loan of db.loans.records) {
    if (!['LAMF', 'LAS'].includes(loan.productId) || !running(loan)) continue;
    const support = holdingsSupportPaise(loan, pct);
    const used = loan.usedPaise ?? 0;
    if (used > support && !loan.marginCall) {
      loan.marginCall = { shortfallPaise: used - support, dueAt: now + 7 * MS_DAY };
      alert(db, `NovaBank: Your collateral value has fallen. Pledge more securities or repay ${inr(used - support)} within 7 days to avoid partial liquidation.`);
    }
  }
  saveDb(db);
};

export const testEndCoolingOff = (): void => {
  const db = loadDb();
  for (const loan of db.loans.records) {
    if (loan.state === 'coolingOff') loan.coolingOffUntil = mockNow(db);
  }
  saveDb(db);
};

export const testVerifyProperty = (loanId: string): void => {
  const db = loadDb();
  const loan = db.loans.records.find((l) => l.id === loanId);
  if (loan) loan.propertyVerified = true;
  saveDb(db);
};

/** Releases the next home-construction stage or education semester (§14). */
export const testReleaseStage = (loanId: string): void => {
  const db = loadDb();
  const loan = db.loans.records.find((l) => l.id === loanId);
  if (!loan) return;
  if (loan.productId === 'HOME' && (loan.stagesPaid ?? 0) < 4) {
    loan.stagesPaid = (loan.stagesPaid ?? 0) + 1;
    credit(db, Math.round(loan.amountPaise / 4), `Home Loan — stage ${loan.stagesPaid} of 4`);
    alert(db, `NovaBank: Stage ${loan.stagesPaid} of 4 of your Home Loan has been released.`);
  }
  saveDb(db);
};

export const testSetGoldRate = (rupeesPerGram: number): void => {
  const db = loadDb();
  db.loans.goldRateRupeesPerGram = rupeesPerGram;
  saveDb(db);
};

export const testSetRestricted = (restricted: boolean): void => {
  const db = loadDb();
  db.loans.restricted = restricted;
  saveDb(db);
};

export const testSetJointHolder = (joint: boolean): void => {
  const db = loadDb();
  db.loans.jointHolder = joint;
  saveDb(db);
};

export const testSetBusinessProfile = (has: boolean): void => {
  const db = loadDb();
  db.loans.businessProfile = has;
  saveDb(db);
};

/** Salary credit: records it for Salary Advance and repays any open advance (§17). */
export const testCreditSalary = (amountRupees: number): void => {
  const db = loadDb();
  db.loans.lastSalaryRupees = amountRupees;
  credit(db, toPaise(amountRupees), 'Salary credit');
  const advance = db.loans.records.find((l) => l.productId === 'SALARY_ADVANCE' && l.state === 'active');
  if (advance) {
    debit(db, advance.outstandingPaise, 'Salary Advance repayment');
    advance.outstandingPaise = 0;
    closeLoan(db, advance, mockNow(db));
  }
  saveDb(db);
};





